import PropTypes from "prop-types";
import cs from "classnames";
import {
  Pill,
  Icon,
  closeIcon,
  Link,
  LinkGenerator
} from "@lululemon/dsp-ecom-pattern-library";
import { DividerHorizontal, IconTextList } from "@lululemon/dsp-legacy-shared";
import { trackEvent } from "@lululemon/ecom-core";
import { StoreHours } from "@lululemon/dsp-components";
import content from "../../../content/content";
import formatDistanceLabel from "../format-distance-label/format-distance-label";
import { convertToCountryCode, useRegionalize } from "@lululemon/mwa-i18n";
import { useRouter } from "next/router";
import styles from "./store-info-card.module.scss";

const cardContent = content.components.storeLocator.storeInfoCard;

const ExperientialPill = () => {
  return (
    <Pill kind="primary" className="my-pill" tag="span">
      Experiential Store
    </Pill>
  );
};

const StoreInfoCard = (props) => {
  const {
    ctaCallback = () => {},
    closeIconClickHandler = () => {},
    distance = 0,
    experientialStoreData,
    fullAddress = "",
    hours = [],
    message = "",
    mapped = false,
    onStoreSelected = () => {},
    publicConfig,
    selectThisStore = false,
    showCarat = false,
    specialHours = [],
    storeId = "",
    storeName = "",
    storeSlug = "",
    storeStatus = "",
    storeType = ""
  } = props;

  const { locale } = useRouter();
  const userLocaleCountry = convertToCountryCode(locale);
  const { regionalizeLink } = useRegionalize();
  const storeUrl = regionalizeLink(`${publicConfig.nav.apiHost}${storeSlug}`);

  // Hide this from template if value is undefined (e.g., function returns empty string).
  const distanceLabel = `${formatDistanceLabel(userLocaleCountry, distance)} ${
    cardContent.distanceLabelSuffix
  }`;
  const preventEvent = (e) => {
    e.stopPropagation();
  };
  const storeCardContent = () => {
    return (
      <>
        <div className={cs("lll-base", styles.contentWrapper)}>
          {mapped && closeIconClickHandler && (
            // this has to be a custom button to match with the styles from the modal component, the ecom pattern library transparent button has a different style
            <button
              type="button"
              onClick={closeIconClickHandler}
              className={styles.closeButton}
            >
              <Icon content={closeIcon} />
            </button>
          )}
          <h2 className={cs("OneLinkNoTx", styles.name)}>{storeName}</h2>
          <div className={styles.description}>
            <span data-testid={`${storeType}StoreTypeLabel`}>
              {storeType === "experiential" ? (
                <ExperientialPill />
              ) : (
                cardContent.typeLabelOther
              )}
            </span>
            {distanceLabel && (
              <>
                <span className="lll-separator" />
                <span data-testid="distanceLabel">{distanceLabel}</span>
              </>
            )}
          </div>
          {experientialStoreData && experientialStoreData.searchSubHeader && (
            <div className={styles.subtitle}>
              {experientialStoreData.searchSubHeader}
            </div>
          )}
          {storeStatus?.toLowerCase() === "active_soon" ? (
            <div className="lll-text-xsmall">Opening Soon</div>
          ) : (
            <div>
              <StoreHours noPadding hours={hours} specialHours={specialHours} />
            </div>
          )}
          {message && (
            <div className={styles.messageContainer}>
              <div className={styles.messageContent}>{message}</div>
            </div>
          )}
          <DividerHorizontal />
          <LinkGenerator
            href={`${publicConfig.googleMapsSearchDomain}${fullAddress}`}
            tag="a"
            target="_blank"
            onClick={preventEvent}
            className={styles.iconOuterContaier}
            data-testid="storeLink"
          >
            <IconTextList
              listItemClass={styles.addressContent}
              listItems={[
                {
                  iconName: "location",
                  text: fullAddress
                }
              ]}
            />
          </LinkGenerator>
        </div>

        {selectThisStore ? (
          <div className={cs("lll-base", styles.ctaWrapper)}>
            <Link
              href={storeUrl}
              onClick={() => {
                const eventKey = mapped
                  ? "select-this-store-mapped"
                  : "select-this-store-listed";
                trackEvent(eventKey, { storeName, storeId });
                onStoreSelected(storeId);
              }}
              className={styles.linkButton}
              tag="a"
              variant="arrowBold"
              direction="right"
            >
              {cardContent.selectThisStore}
            </Link>
          </div>
        ) : (
          <div className={cs("lll-base", styles.ctaWrapper)}>
            <Link
              href={storeUrl}
              tag={({ href, ...propsObj }) => (
                <LinkGenerator href={href} {...propsObj} />
              )}
              onClick={ctaCallback}
              variant="arrowBold"
              direction="right"
            >
              {cardContent.storeLinkLabel.toUpperCase()}
            </Link>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      {selectThisStore ? (
        <LinkGenerator
          data-testid="selectThisStore"
          className={cs(
            styles.storeInfoCardContainer,
            showCarat ? styles.showCarat : ""
          )}
          onClick={(e) => {
            const eventKey = mapped
              ? "select-this-store-mapped"
              : "select-this-store-listed";
            trackEvent(eventKey, { storeName, storeId });
            onStoreSelected(storeId);
            e.stopPropagation();
            return true;
          }}
        >
          {storeCardContent()}
        </LinkGenerator>
      ) : (
        <LinkGenerator
          href={storeSlug}
          tag="div"
          className={cs(
            styles.storeInfoCardContainer,
            showCarat ? styles.showCarat : ""
          )}
        >
          {storeCardContent()}
        </LinkGenerator>
      )}
    </>
  );
};

StoreInfoCard.propTypes = {
  ctaCallback: PropTypes.func,
  /* Distance in meters from location entered in LocationPicker */
  distance: PropTypes.number.isRequired,
  fullAddress: PropTypes.string.isRequired,
  hours: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      closeHour: PropTypes.string,
      openHour: PropTypes.string
    })
  ).isRequired,
  message: PropTypes.string,
  specialHours: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      closeHour: PropTypes.string,
      openHour: PropTypes.string,
      description: PropTypes.string
    })
  ),
  storeName: PropTypes.string.isRequired,
  storeSlug: PropTypes.string.isRequired,
  storeType: PropTypes.string,
  closeIconClickHandler: PropTypes.func,
  showCarat: PropTypes.bool,
  storeStatus: PropTypes.string,
  selectThisStore: PropTypes.bool,
  onStoreSelected: PropTypes.func,
  storeId: PropTypes.string,
  publicConfig: PropTypes.shape({
    googleMapsSearchDomain: PropTypes.string,
    googleMapsApiKey: PropTypes.string,
    nav: {
      apiHost: PropTypes.string,
      apiHostBasePath: PropTypes.string
    }
  }).isRequired,
  mapped: PropTypes.bool,
  experientialStoreData: PropTypes.shape({
    searchSubHeader: PropTypes.string,
    cafeSpaceImageUrl: PropTypes.string,
    eventRentalEmailUrl: PropTypes.string,
    foodOrderUrl: PropTypes.string,
    fuelMenuSlug: PropTypes.string,
    searchSubHeader: PropTypes.string,
    storeSpaceImageUrl: PropTypes.string,
    studioSpaceImageUrl: PropTypes.string,
    subHeader: PropTypes.string
  })
};

export default StoreInfoCard;
